const CACHE_KEY_PREFIX = "plotcat_prompt_cache_v1";
const CACHE_INDEX_KEY = "plotcat_prompt_cache_index_v1";
const DEFAULT_MAX_ENTRIES = 50;
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

type CacheEntry = {
  key: string;
  createdAt: number;
};

type CachedResponse = {
  value: string;
  createdAt: number;
};

function safeParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function hashString(input: string): string {
  let hash = 5381;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 33) ^ input.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
}

function buildCacheKey(system: string, user: string, model: string): string {
  const raw = `${system}\n---\n${user}\n---\n${model}`;
  return `${CACHE_KEY_PREFIX}:${hashString(raw)}`;
}

function loadIndex(): CacheEntry[] {
  return safeParse<CacheEntry[]>(localStorage.getItem(CACHE_INDEX_KEY), []);
}

function saveIndex(entries: CacheEntry[]) {
  localStorage.setItem(CACHE_INDEX_KEY, JSON.stringify(entries));
}

function pruneIndex(entries: CacheEntry[], maxEntries: number, ttlMs: number): CacheEntry[] {
  const now = Date.now();
  const filtered = entries.filter((entry) => now - entry.createdAt <= ttlMs);
  if (filtered.length <= maxEntries) return filtered;
  const sorted = [...filtered].sort((a, b) => a.createdAt - b.createdAt);
  return sorted.slice(sorted.length - maxEntries);
}

export function getCachedResponse(
  system: string,
  user: string,
  model: string,
  ttlMs: number = DEFAULT_TTL_MS,
): string | null {
  const key = buildCacheKey(system, user, model);
  const cached = safeParse<CachedResponse>(localStorage.getItem(key), { value: "", createdAt: 0 });
  if (!cached.value) return null;
  if (Date.now() - cached.createdAt > ttlMs) {
    localStorage.removeItem(key);
    return null;
  }
  return cached.value;
}

export function setCachedResponse(
  system: string,
  user: string,
  model: string,
  response: string,
  maxEntries: number = DEFAULT_MAX_ENTRIES,
  ttlMs: number = DEFAULT_TTL_MS,
): void {
  const key = buildCacheKey(system, user, model);
  const now = Date.now();
  const payload: CachedResponse = { value: response, createdAt: now };
  localStorage.setItem(key, JSON.stringify(payload));

  const index = loadIndex();
  const withoutExisting = index.filter((entry) => entry.key !== key);
  const nextIndex = pruneIndex([...withoutExisting, { key, createdAt: now }], maxEntries, ttlMs);
  saveIndex(nextIndex);

  const keepKeys = new Set(nextIndex.map((entry) => entry.key));
  for (const entry of withoutExisting) {
    if (!keepKeys.has(entry.key)) {
      localStorage.removeItem(entry.key);
    }
  }
}

export function clearPromptCache() {
  const index = loadIndex();
  for (const entry of index) {
    localStorage.removeItem(entry.key);
  }
  localStorage.removeItem(CACHE_INDEX_KEY);
}
